import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { Info, Pencil } from 'lucide-react';
import { useParkingQueueRules } from '../../hooks/parkingQueue/useParkingQueueRules';
import { useUserNames } from '../../Helpers/useUserNames';
import { PARKING_QUEUE_RULES_MAX_LENGTH } from '../../types/parkingQueueRules';
import './ParkingQueue.css';

/**
 * "Regler för kön": shared working rules shown below the Parkering tabs on the
 * Kö tab. Plain text, clamped to a few lines; every admin (everyone who sees
 * the page) edits it in place with the pencil. Shows who last changed it.
 * `readOnly` (the spot modal's Kö section): no pencil, nothing at all while
 * no text is written.
 */
const ParkingQueueRulesBox: React.FC<{ readOnly?: boolean }> = ({ readOnly = false }) => {
    const { rules, loading, saving, error, save } = useParkingQueueRules();
    const { userNames, loadUserNames } = useUserNames();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    const text = rules?.text ?? '';

    useEffect(() => {
        if (rules?.updatedBy) void loadUserNames([rules.updatedBy]);
    }, [rules?.updatedBy, loadUserNames]);

    // "Visa mer" only when the clamped text is cut off.
    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return;
        const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text, expanded, editing]);

    if (loading || (readOnly && !text)) return null;

    const startEdit = () => {
        setDraft(text);
        setEditing(true);
    };

    const handleSave = async () => {
        try {
            await save(draft);
            setEditing(false);
            setExpanded(false);
        } catch {
            // error shown from the hook
        }
    };

    const editorName = rules ? (userNames[rules.updatedBy] ?? '…') : null;

    return (
        <div className={`parking-queue-rules${readOnly ? ' is-read-only' : ''}`} aria-label="Regler för kön">
            <div className="parking-queue-rules-header">
                <Info size={16} className="parking-queue-rules-icon" aria-hidden />
                <span className="parking-queue-rules-title">Regler för kön</span>
                {!editing && !readOnly && (
                    <button type="button" className="parking-queue-rules-edit" onClick={startEdit} aria-label="Redigera regler för kön" title="Redigera">
                        <Pencil size={14} />
                    </button>
                )}
            </div>
            {error && <Alert variant="danger" className="py-1 px-2 mb-2 small">{error}</Alert>}
            {editing ? (
                <>
                    <Form.Control
                        as="textarea"
                        rows={6}
                        value={draft}
                        maxLength={PARKING_QUEUE_RULES_MAX_LENGTH}
                        onChange={e => setDraft(e.target.value)}
                        placeholder="Skriv hur ni arbetar med kön, t.ex. vem som får plats först och när avvikelser är okej."
                        autoFocus
                        disabled={saving}
                    />
                    <div className="parking-queue-rules-actions">
                        <Button variant="outline-secondary" size="sm" onClick={() => setEditing(false)} disabled={saving}>Avbryt</Button>
                        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Sparar…' : 'Spara'}</Button>
                    </div>
                </>
            ) : text ? (
                <>
                    <div ref={textRef} className={`parking-queue-rules-text${expanded ? '' : ' is-clamped'}`}>{text}</div>
                    {(overflows || expanded) && (
                        <button type="button" className="parking-queue-rules-more" onClick={() => setExpanded(x => !x)}>
                            {expanded ? 'Visa mindre' : 'Visa mer'}
                        </button>
                    )}
                </>
            ) : (
                <div className="parking-queue-rules-empty">Inga köregler har skrivits än.</div>
            )}
            {!editing && rules && (
                <div className="parking-queue-rules-meta">Senast ändrad av {editorName} {rules.updatedWhen.slice(0, 16)}</div>
            )}
        </div>
    );
};

export default ParkingQueueRulesBox;
